import { useState, useMemo } from 'react'
import { formatTimeRange, readableDescription, toMapLink } from '../lib/searchUtils.js'
import { getEventPinConfig, getPlacePinConfig } from '../lib/pinConfig.js'
import ImageCarousel from './ImageCarousel.jsx'

export default function DetailCard({ item, variant = 'card', isSelected, onSelect, onClose }) {
    const [copySuccess, setCopySuccess] = useState(false)

    // Randomly assign one of 3 card styles (stable across re-renders)
    const cardStyle = useMemo(() => {
        if (!item?.id) return 1
        // Use item ID to generate consistent random style
        const hash = item.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
        return (hash % 3) + 1 // Returns 1, 2, or 3
    }, [item?.id])

    if (!item) {
        return null
    }

    const categoryLabel = item?.category ?? (item?.searchType === 'places' ? 'Place' : 'Event')
    const pillClasses = item?.searchType === 'places'
        ? 'bg-blue-100 text-blue-700'
        : 'bg-purple-100 text-purple-700'
    const pinConfig = item?.searchType === 'places'
        ? getPlacePinConfig(item?.category ?? item?.ml_category)
        : getEventPinConfig(item?.category ?? item?.ml_category)
    const description = readableDescription(item?.description)
    const timeRange = item?.searchType === 'events'
        ? formatTimeRange(item?.time_start, item?.time_end)
        : null
    const mapsLink = toMapLink(item)
    const linkLabel = item?.searchType === 'places' ? 'More info' : 'Event details'
    const images = Array.isArray(item?.images) ? item.images : (item?.image ? [item.image] : [])
    const primaryUrl = item?.url ?? (Array.isArray(item?.urls) ? item.urls.find(Boolean) : null)
    const reservationUrl = Array.isArray(item?.reservation_url)
        ? item.reservation_url.find(Boolean)
        : null
    const socialLink = Array.isArray(item?.social_link)
        ? item.social_link.find(Boolean)
        : null
    const phone = Array.isArray(item?.telephone)
        ? item.telephone.find(Boolean)
        : null
    const email = Array.isArray(item?.email)
        ? item.email.find(Boolean)
        : null
    const telHref = phone ? `tel:${phone.replace(/[^0-9+]/g, '')}` : null
    const emailHref = email ? `mailto:${email}` : null

    const linkItems = []
    if (primaryUrl) {
        linkItems.push({ href: primaryUrl, label: linkLabel })
    }
    if (reservationUrl && reservationUrl !== primaryUrl) {
        linkItems.push({ href: reservationUrl, label: 'Reserve' })
    }
    if (socialLink && !linkItems.some(link => link.href === socialLink)) {
        linkItems.push({ href: socialLink, label: 'Social' })
    }

    // Generate shareable link
    const shareLink = item?.searchType === 'places' 
        ? `${window.location.origin}/place/${item.id}`
        : `${window.location.origin}/event/${item.id}`

    const handleShare = async (event) => {
        event?.stopPropagation?.()
        try {
            await navigator.clipboard.writeText(shareLink)
            setCopySuccess(true)
            setTimeout(() => setCopySuccess(false), 2000)
        } catch (err) {
            console.error('Failed to copy link:', err)
        }
    }

    // Overlay variant (for map detail)
    if (variant === 'overlay') {
        return (
            <div className="pointer-events-auto absolute right-4 top-4 z-20 w-80 max-w-full rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl">
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute right-2 top-2 rounded-full p-1 text-slate-400 transition hover:text-slate-600"
                    aria-label="Close details"
                >
                    ×
                </button>
                <div className="space-y-3 pr-6">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${pillClasses}`}>
                        {categoryLabel}
                    </span>
                    <h3 className="text-lg font-semibold text-slate-900">
                        {pinConfig.glyph} {item?.name ?? item?.place_name ?? 'Untitled'}
                    </h3>
                    <ImageCarousel
                        images={images}
                        alt={item?.name ? `${item.name} artwork` : 'Listing artwork'}
                        className="h-40 w-full rounded-xl object-cover"
                    />
                    {description && <p className="text-sm leading-relaxed text-slate-600">{description}</p>}
                    {(item?.place_name || item?.address) && (
                        <p className="text-sm text-slate-500">
                            {item?.place_name}
                            {item?.place_name && item?.address ? ' · ' : ''}
                            {item?.address}
                        </p>
                    )}
                    {timeRange && <p className="text-sm font-medium text-slate-700">{timeRange}</p>}
                    {(phone || email) && (
                        <div className="space-y-1 text-sm text-slate-500">
                            {phone && telHref && (
                                <p>
                                    Phone:{' '}
                                    <a
                                        href={telHref}
                                        className="text-logo-pink transition hover:text-purple-600"
                                    >
                                        {phone}
                                    </a>
                                </p>
                            )}
                            {email && emailHref && (
                                <p>
                                    Email:{' '}
                                    <a
                                        href={emailHref}
                                        className="text-logo-pink transition hover:text-purple-600"
                                    >
                                        {email}
                                    </a>
                                </p>
                            )}
                        </div>
                    )}
                    <div className="flex flex-wrap gap-3 text-sm font-medium">
                        {linkItems.map(link => (
                            <a
                                key={link.href}
                                href={link.href}
                                target="_blank"
                                rel="noreferrer noopener"
                                className="text-logo-pink transition hover:text-purple-600"
                            >
                                {link.label}
                            </a>
                        ))}
                        {mapsLink && (
                            <a
                                href={mapsLink}
                                target="_blank"
                                rel="noreferrer noopener"
                                className="flex items-center gap-1 text-logo-pink transition hover:text-purple-600"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-5 w-5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                                </svg>
                                {item?.place_name || item?.address || 'View on map'}
                            </a>
                        )}
                        <button
                            type="button"
                            onClick={handleShare}
                            className="flex items-center gap-1 text-logo-pink transition hover:text-purple-600"
                            title="Copy shareable link"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-5 w-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z" />
                            </svg>
                            {copySuccess ? 'Copied!' : 'Share'}
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    // Card variant (for results grid)
    const cardId = item.id

    // Common content components
    const titleWithCategory = (
        <h3 className="flex items-center gap-2 text-lg font-semibold">
            <span className={`text-xl ${item?.searchType === 'places' ? 'text-blue-600' : 'text-purple-600'}`}>
                {pinConfig.glyph}
            </span>
            <a
                href={primaryUrl || '#'}
                target="_blank"
                rel="noreferrer noopener"
                className="text-slate-900 transition hover:text-purple-600 hover:underline"
                onClick={event => event.stopPropagation()}
            >
                {item?.name ?? item?.place_name ?? 'Untitled'}
            </a>
            <button
                type="button"
                onClick={handleShare}
                className={`ml-auto flex-shrink-0 transition ${copySuccess ? 'text-green-600' : 'text-slate-400 hover:text-purple-600'}`}
                title="Copy shareable link"
            >
                {copySuccess ? (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="h-5 w-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                    </svg>
                ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-5 w-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z" />
                    </svg>
                )}
            </button>
        </h3>
    )

    const descriptionElement = description && (
        <p className="text-sm leading-relaxed text-slate-600">{description}</p>
    )

    const timeElement = timeRange && <p className="text-sm font-medium text-slate-700">{timeRange}</p>

    // Full address text for map link
    const fullAddressText = [item?.place_name, item?.address].filter(Boolean).join(' · ') || 'View on map'

    const contactInfo = (phone || email) && (
        <div className="space-y-1 text-sm text-slate-500">
            {phone && telHref && (
                <p>
                    Phone:{' '}
                    <a
                        href={telHref}
                        className="text-logo-pink transition hover:text-purple-600"
                        onClick={event => event.stopPropagation()}
                    >
                        {phone}
                    </a>
                </p>
            )}
            {email && emailHref && (
                <p>
                    Email:{' '}
                    <a
                        href={emailHref}
                        className="text-logo-pink transition hover:text-purple-600"
                        onClick={event => event.stopPropagation()}
                    >
                        {email}
                    </a>
                </p>
            )}
        </div>
    )

    // Filter out primary URL from additional links (it's now in title)
    const additionalLinks = linkItems.filter(link => link.href !== primaryUrl)

    const linksSection = (additionalLinks.length > 0 || mapsLink) && (
        <div className="mt-auto flex flex-wrap gap-3 pt-3 text-sm font-medium">
            {additionalLinks.map(link => (
                <a
                    key={link.href}
                    href={link.href}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-logo-pink transition hover:text-purple-600"
                    onClick={event => event.stopPropagation()}
                >
                    {link.label}
                </a>
            ))}
            {mapsLink && (
                <a
                    href={mapsLink}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="flex items-center gap-1 text-logo-pink transition hover:text-purple-600"
                    onClick={event => event.stopPropagation()}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="h-5 w-5"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                        />
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"
                        />
                    </svg>
                    {fullAddressText}
                </a>
            )}
        </div>
    )

    // Style 1: Image on the right (compact, current style)
    if (cardStyle === 1) {
        return (
            <button
                type="button"
                data-result-id={cardId}
                onClick={() => onSelect(cardId)}
                className={`relative flex h-full flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 ${
                    isSelected ? 'highlighted-item' : ''
                }`}
            >
                <div className="flex items-start justify-between gap-3">
                    {titleWithCategory}
                    <ImageCarousel
                        images={images}
                        alt={item?.name ? `${item.name} artwork` : 'Listing artwork'}
                        className="h-16 w-16 flex-shrink-0 rounded-lg object-cover"
                    />
                </div>
                {descriptionElement}
                {timeElement}
                {contactInfo}
                {linksSection}
            </button>
        )
    }

    // Style 2: Image on top (magazine style)
    if (cardStyle === 2) {
        return (
            <button
                type="button"
                data-result-id={cardId}
                onClick={() => onSelect(cardId)}
                className={`relative flex h-full flex-col gap-3 rounded-2xl border-2 border-slate-200 bg-white text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 ${
                    isSelected ? 'highlighted-item' : ''
                }`}
            >
                <ImageCarousel
                    images={images}
                    alt={item?.name ? `${item.name} artwork` : 'Listing artwork'}
                    className="h-32 w-full rounded-t-xl object-cover"
                />
                <div className="flex flex-col gap-3 p-4 pt-0">
                    {titleWithCategory}
                    {descriptionElement}
                    {timeElement}
                    {contactInfo}
                    {linksSection}
                </div>
            </button>
        )
    }

    // Style 3: Image on the left (horizontal card)
    return (
        <button
            type="button"
            data-result-id={cardId}
            onClick={() => onSelect(cardId)}
            className={`relative flex h-full flex-row gap-4 rounded-2xl border border-slate-300 bg-gradient-to-br from-white to-slate-50 p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 ${
                isSelected ? 'highlighted-item' : ''
            }`}
        >
            <ImageCarousel
                images={images}
                alt={item?.name ? `${item.name} artwork` : 'Listing artwork'}
                className="h-24 w-24 flex-shrink-0 rounded-xl object-cover"
            />
            <div className="flex min-w-0 flex-1 flex-col gap-3">
                {titleWithCategory}
                {descriptionElement}
                {timeElement}
                {contactInfo}
                {linksSection}
            </div>
        </button>
    )
}
